import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { invitationAcceptSchema, type InvitationAcceptInput } from '@daater/shared';
import { Button } from '@daater/ui';
import {
  useAcceptInvitationMutation,
  useLoginMutation,
  useVerifyInvitationQuery,
} from '../api/queries';
import { fetchCurrentAccount } from '../api/endpoints';
import {
  buildPaymentRedirectSearch,
  resolveCurrentAccountAccess,
} from '../api/current-account-access';
import DaaterLogoHomeLink from '../components/daater-logo-home-link';
import { useAuthStore } from '../lib/auth-store';
import { useAccountStore } from '../lib/account-store';

export default function InvitationScreen() {
  const navigate = useNavigate();
  const setAuth = useAuthStore(state => state.setAuth);
  const setAccount = useAccountStore(state => state.setAccount);
  const acceptMutation = useAcceptInvitationMutation();
  const loginMutation = useLoginMutation();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const token =
    typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('token') ?? '')
      : '';

  const { data: invitationData, isLoading, error: verifyError } = useVerifyInvitationQuery(token);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InvitationAcceptInput>({
    resolver: zodResolver(invitationAcceptSchema),
    values: invitationData
      ? {
          firstName: '',
          lastName: '',
          email: invitationData.email ?? '',
          password: '',
          passwordConfirm: '',
        }
      : undefined,
  });

  React.useEffect(() => {
    if (invitationData?.isConfirmed) {
      void navigate({ to: '/login', replace: true });
    }
  }, [invitationData, navigate]);

  const onSubmit = async (values: InvitationAcceptInput) => {
    if (!invitationData) return;
    setErrorMessage(null);

    try {
      await acceptMutation.mutateAsync({
        _id: invitationData._id,
        email: invitationData.email,
        account: invitationData.account,
        team: invitationData.team,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
        passwordConfirm: values.passwordConfirm,
      });

      // Auto-login after accepting invitation
      const loginResponse = await loginMutation.mutateAsync({
        email: invitationData.email,
        password: values.password,
      });

      setAuth(loginResponse.user, loginResponse.token);

      const access = await resolveCurrentAccountAccess(fetchCurrentAccount);
      if (access.account) {
        setAccount(access.account);
      }

      if (access.decision === 'payment') {
        void navigate({
          to: '/payment',
          replace: true,
          search: buildPaymentRedirectSearch('', access.amount),
        });
        return;
      }

      if (access.decision === 'login') {
        throw new Error('Tu sesión expiró. Ingresa nuevamente para continuar.');
      }

      if (access.decision === 'error') {
        throw new Error('No se pudo validar el estado de la cuenta.');
      }

      void navigate({ to: '/', replace: true });
    } catch (error) {
      setErrorMessage((error as Error).message || 'Error al aceptar la invitación');
    }
  };

  const isPending = acceptMutation.isPending || loginMutation.isPending;

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pale-grey px-4">
        <div className="w-full max-w-md rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
          <div className="rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-3 text-sm text-darkish-pink">
            Token de invitación no proporcionado.
          </div>
          <button
            type="button"
            onClick={() => void navigate({ to: '/login' })}
            className="mt-4 text-xs text-slate underline"
          >
            Ir al login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Left decorative panel */}
      <div className="hidden bg-[#1a2e58] px-12 text-white lg:flex lg:w-1/2 lg:flex-col lg:items-center lg:justify-center">
        <div className="mx-auto max-w-md space-y-8">
          <DaaterLogoHomeLink
            alt="Daater"
            className="inline-flex w-fit"
            imgClassName="h-12 brightness-0 invert"
          />
          <blockquote className="border-l-2 border-white/30 pl-5 text-lg leading-relaxed text-white/80">
            Has sido invitado a unirte a un equipo en Daater.
          </blockquote>
          <p className="text-sm text-white/50">
            Completa tu registro para acceder a la plataforma de inteligencia de datos de comercio
            exterior.
          </p>
        </div>
      </div>

      {/* Right side — form */}
      <div className="flex w-full items-center justify-center bg-pale-grey px-4 lg:w-1/2">
        <div className="w-full max-w-md rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
          {/* Logo visible only on mobile */}
          <div className="mb-6 flex justify-center lg:hidden">
            <DaaterLogoHomeLink alt="Daater" imgClassName="h-10" />
          </div>

          <h1 className="text-2xl font-semibold text-charcoal-grey">Aceptar invitación</h1>
          <p className="mt-1 text-sm text-slate">Completa tus datos para unirte al equipo.</p>

          {isLoading ? (
            <p className="mt-6 text-sm text-slate">Verificando invitación...</p>
          ) : verifyError ? (
            <div className="mt-6 rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-3 text-sm text-darkish-pink">
              {verifyError.message || 'Invitación inválida o expirada.'}
            </div>
          ) : invitationData && !invitationData.isConfirmed ? (
            <form className="mt-6 space-y-4" onSubmit={event => void handleSubmit(onSubmit)(event)}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase text-slate">Nombre</label>
                  <input
                    type="text"
                    {...register('firstName')}
                    className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                    placeholder="Juan"
                  />
                  {errors.firstName ? (
                    <p className="mt-1 text-xs text-darkish-pink">{errors.firstName.message}</p>
                  ) : null}
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-slate">Apellido</label>
                  <input
                    type="text"
                    {...register('lastName')}
                    className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                    placeholder="Pérez"
                  />
                  {errors.lastName ? (
                    <p className="mt-1 text-xs text-darkish-pink">{errors.lastName.message}</p>
                  ) : null}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-slate">Correo</label>
                <input
                  type="email"
                  {...register('email')}
                  disabled
                  className="mt-1 w-full rounded-md border border-pale-grey-three bg-pale-grey px-3 py-2 text-sm text-slate"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-slate">Contraseña</label>
                <input
                  type="password"
                  {...register('password')}
                  className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                  placeholder="••••••••"
                />
                {errors.password ? (
                  <p className="mt-1 text-xs text-darkish-pink">{errors.password.message}</p>
                ) : null}
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-slate">
                  Confirmar contraseña
                </label>
                <input
                  type="password"
                  {...register('passwordConfirm')}
                  className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                  placeholder="••••••••"
                />
                {errors.passwordConfirm ? (
                  <p className="mt-1 text-xs text-darkish-pink">{errors.passwordConfirm.message}</p>
                ) : null}
              </div>

              {errorMessage ? (
                <p className="rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-2 text-xs text-darkish-pink">
                  {errorMessage}
                </p>
              ) : null}

              <Button type="submit" className="w-full" disabled={isPending}>
                {isPending ? 'Procesando...' : 'Aceptar invitación'}
              </Button>
            </form>
          ) : null}

          <button
            type="button"
            onClick={() => void navigate({ to: '/login' })}
            className="mt-4 text-xs text-slate underline"
          >
            Ir al login
          </button>
        </div>
      </div>
    </div>
  );
}
